import { useCallback, useEffect, useRef } from 'react';

import { debounce } from '../01-core/Utilities';
import { ButtonProps } from '../02-atoms/Button';
import { ButtonGroup } from '../03-molecules/ButtonGroup';

export interface StickyActionsProps {
  /** Actions */
  actions?: ButtonProps[];
  /** Additional class */
  className?: string;
}

/** A sticky actions panel */
export const StickyActions = ({ actions, className }: StickyActionsProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const onResize = useCallback(() => {
    if (!ref.current) return;
    const height = ref.current.getBoundingClientRect().height;
    document
      .getElementsByTagName('body')[0]
      .style.setProperty('--sticky-actions--height', Math.floor(height) + 'px');
  }, [ref]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    debounce(
      () => {
        onResize();
      },
      100,
      false,
    ),
    [onResize],
  ); // some debounce typings expect the 3rd arg

  useEffect(() => {
    window.addEventListener('resize', handleResize as unknown as EventListener);
    window.requestAnimationFrame(() => {
      onResize();
    });
    return () => {
      window.removeEventListener(
        'resize',
        handleResize as unknown as EventListener,
      );
      document
        .getElementsByTagName('body')[0]
        .style.removeProperty('--sticky-actions--height');
    };
  }, [ref, handleResize, onResize]);

  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <div ref={ref} className={['sticky-actions', className].join(' ')}>
      <ButtonGroup className="sticky-actions__buttons" items={actions} />
    </div>
  );
};
